import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "../components/common/PageTitle";
import { apiService } from "../api/services/apiService";
import type { KafkaEventTopic } from "../types";
import { KafkaSchemaFormat } from "../types";

function formatSchemaContent(topic: KafkaEventTopic): string {
  if (topic.schemaFormat === KafkaSchemaFormat.JSON) {
    try {
      return JSON.stringify(JSON.parse(topic.schemaContent), null, 2);
    } catch {
      return topic.schemaContent;
    }
  }
  return topic.schemaContent;
}

const KafkaEventExplorerDetailPage: React.FC = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const [topic, setTopic] = useState<KafkaEventTopic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!topicId) return;
    setLoading(true);
    apiService
      .getKafkaEventTopic(topicId)
      .then(setTopic)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Failed to load topic");
      })
      .finally(() => setLoading(false));
  }, [topicId]);

  const formattedSchema = useMemo(
    () => (topic ? formatSchemaContent(topic) : ""),
    [topic],
  );

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <PageTitle
        pageTitle={topic?.name ?? "Kafka Event Topic"}
        pageDescription={
          topic?.purpose?.trim() || "Read-only Kafka topic and schema view."
        }
      />

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/kafka-event-explorer")}
          sx={{ mb: 3, textTransform: "none" }}
        >
          Back to Kafka Event Explorer
        </Button>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {topic && !loading && (
          <>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
              <Chip label={topic.status} color="primary" size="small" />
              <Chip label={topic.environment} size="small" variant="outlined" />
              <Chip label={topic.schemaFormat} size="small" variant="outlined" />
              {topic.schemaVersion && (
                <Chip label={`v${topic.schemaVersion}`} size="small" variant="outlined" />
              )}
              {topic.compatibilityLevel && (
                <Chip label={topic.compatibilityLevel} size="small" variant="outlined" />
              )}
              {topic.dataSensitivity && (
                <Chip label={topic.dataSensitivity} size="small" variant="outlined" />
              )}
            </Box>

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Purpose:</strong> {topic.purpose || "—"}
              </Typography>
              {topic.description && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Description:</strong> {topic.description}
                </Typography>
              )}
              {topic.schemaSubject && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Schema subject:</strong> {topic.schemaSubject}
                </Typography>
              )}
              {topic.schemaRegistryUrl && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Registry URL:</strong> {topic.schemaRegistryUrl}
                </Typography>
              )}
              {(topic.partitionCount != null ||
                topic.replicationFactor != null ||
                topic.retentionMs != null) && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Topic config:</strong>{" "}
                  {[
                    topic.partitionCount != null
                      ? `partitions=${topic.partitionCount}`
                      : null,
                    topic.replicationFactor != null
                      ? `replication=${topic.replicationFactor}`
                      : null,
                    topic.retentionMs != null ? `retentionMs=${topic.retentionMs}` : null,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </Typography>
              )}
              {topic.apis?.length > 0 && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Linked APIs:</strong>{" "}
                  {topic.apis.map((a) => a.name).join(", ")}
                </Typography>
              )}
              {topic.products?.length > 0 && (
                <Typography variant="body2">
                  <strong>Linked products:</strong>{" "}
                  {topic.products.map((p) => p.name).join(", ")}
                </Typography>
              )}
            </Paper>

            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Value schema
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  overflow: "auto",
                  fontSize: "0.85rem",
                  maxHeight: 480,
                }}
              >
                {formattedSchema}
              </Box>
            </Paper>

            {topic.keySchemaContent && (
              <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Key schema
                  {topic.keySchemaFormat ? ` (${topic.keySchemaFormat})` : ""}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.85rem",
                    maxHeight: 320,
                  }}
                >
                  {topic.keySchemaContent}
                </Box>
              </Paper>
            )}

            {topic.samplePayload && (
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Sample payload
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    overflow: "auto",
                    fontSize: "0.85rem",
                  }}
                >
                  {topic.samplePayload}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default KafkaEventExplorerDetailPage;
